import { h, ref } from 'vue';
import { createColumnHelper } from '@tanstack/vue-table';
import { Checkbox, TooltipPlugin } from '@n11/so-ui-components';
import ProductInfoCell from '../DataTable/ProductInfoCell';
import FinalPriceCell from '../DataTable/FinalPriceCell';
import Label from '../DataTable/Label';
import ErrorLabel from '../DataTable/ErrorLabel';
import PopoverInput from '../DataTable/PopoverInput';
import Status from '../DataTable/Status';
import Sender from '../DataTable/Sender';
import ActionButton from '../DataTable/ActionButton';
import ActionButtons from '../DataTable/ActionButtons';
import TableHeadTooltip from '../DataTable/TableHeadTooltip';
import Request from '@/services/request';
import { sendProductsDetailUpdateEvent, sendGoToProductDetailClickEvent } from '@/utils/ga4/helper';
import { COLUMNS, COLUMN_NAMES } from '@/utils/enum/tableColumns';
import { PRODUCT_DETAIL_SHOW_ENABLE } from '@/utils/enum/configs';

const request = new Request();
const isUpdateProductsLoading = ref(false);

const handleButtonClick = (store, router, pid, isMatchPending) => {
  if (!pid) return;

  sendGoToProductDetailClickEvent();

  if (isMatchPending) {
    window.location.href = `/selleroffice/product/products?pid=${pid}`;
    return;
  }

  const config = store.getters['common/getConfigList'] || {};
  const productDetailEnable = config?.[PRODUCT_DETAIL_SHOW_ENABLE];
  const isProductDetailEnabled = typeof productDetailEnable === 'string' 
    ? productDetailEnable === 'true' 
    : !!productDetailEnable;
  
  if (isProductDetailEnabled) {
    const route = router.resolve({ name: "ProductDetail", params: { pid } });
    window.open(route.href, '_blank');
  } else {
    window.location.href = `/selleroffice/product/products?pid=${pid}`;
  }
};

const handleUpdate = async (store, body, type) => {
  isUpdateProductsLoading.value = true;
  try {
    await store.dispatch("products/handleUpdate", body);
    sendProductsDetailUpdateEvent({
      update_area: store.getters["products/getProductListActiveTab"],
      update_tab: type ?? "",
    });

    isUpdateProductsLoading.value = false;
  } catch (e) {
    isUpdateProductsLoading.value = false;
    throw new Error(e);
  }
};

const handleProductDetail = async (pid) => {
  try {
    const response = await request.get(
      `endpoint-products/product-detail-page-url/${pid}`,
    );
    response.data.productUrl && window.open(response.data.productUrl, "_blank");
  } catch (error) {
    console.error(error);
  }
};

let interval = null;
export function columnService({ store, refreshCallback, activeTab, router }) {
  const columnHelper = createColumnHelper();

  let isFormValid = true;

  function formatPrice(price) {
    const formattedPrice = parseFloat(price).toLocaleString("tr-TR", { style: 'currency', currency: 'TRY' })
    return formattedPrice.includes(',00') ? formattedPrice.replace(',00', '') : formattedPrice
  }

  function validatePrice(value, listPrice, maxPrice, salesPrice) {
    const numericValue = parseFloat(value);

    if (numericValue === 0) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "Girilen Satış fiyatı 0 olamaz."
        );
      }, 500);
      return false;
    }

    if (numericValue > parseFloat(listPrice)) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "n11 Satış Fiyatı PSF'den büyük olamaz."
        );
      }, 500);
      return false;
    }

    if (numericValue == salesPrice) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "Girilen Satış fiyatı, mevcut satış fiyatı ile aynı olamaz."
        );
      }, 500);
      return false;
    }

    if (maxPrice !== undefined && numericValue > parseFloat(maxPrice)) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "Girilen satış fiyatı eksi değer olamaz."
        );
      }, 500);
      return false;
    }

    return true;
  }

  function validateStock(value) {
    const numericValue = parseFloat(value);

    if (isNaN(value)) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "Girilen değer sadece numerik olmalıdır."
        );
      }, 500);
      return false;
    }

    if (numericValue < 0) {
      interval && clearTimeout(interval);
      interval = setTimeout(() => {
        store.commit("products/SET_IS_PRICE_AREA_STATUS", false);
        store.commit(
          "products/SET_IS_PRICE_AREA_ERROR_MESSAGE",
          "Girilen stok değeri eksi olamaz."
        );
      }, 500);
      return false;
    }

    return true;
  }

  const updateFormValidity = (validity) => {
    isFormValid = isFormValid && validity;
  };

  const columns = ref([
    columnHelper.accessor('select-col', {
      header: generateCheckboxHeader,
      cell: generateCheckboxCell,
      size: 44,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Seçim Yap',
          detachable: false,
          sortable: false,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.SELECT_COLUMN),
        },
      },
    }),

    columnHelper.accessor('productName', {
      header: () => 'Ürün Bilgisi',
      cell: ({ row }) => {
        const pid = row.original.id;
        const status = row.original.status;
        return h(ProductInfoCell, {
          productInfo: generateProductInfo(row),
          isClickable: status === 'Active',
          onClick: () => status === 'Active' ? handleProductDetail(pid) : {}
        });
      },
      size: 240,
      isPinned: true,
      enablePinning: true,
      enableResizing: true,
      columnDef: {
        meta: {
          title: 'Ürün Bilgisi',
          detachable: false,
          sortable: false,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.PRODUCT_INFO__WITH_COMISSION),
        },
      },
    }),

    columnHelper.accessor('stockCode', {
      header: () => 'Stok Kodu',
      cell: ({ getValue }) => h(Label, { content: getValue() || '' }),
      size: 120,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Stok Kodu',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.STOCK_CODE),
        },
      },
    }),

    columnHelper.accessor('finalPrice', {
      header: () => h(TableHeadTooltip, {
        title: 'İndirimli Son Fiyat ',
        tooltipText: 'Kullanıcıya gösterilen indirimli son fiyattır.',
        style: 'justify-content: space-between',
        tooltipWidth: 150,
      }),
      cell: ({ row }) => {
        return h(FinalPriceCell, {
          finalPrice: row.original.finalPrice,
          subsidy: row.original.subsidy
        });
      },
      size: 220,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'İndirimli Son Fiyat',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.FINAL_PRICE),
        },
      },
    }),

    columnHelper.accessor('catalogId', {
      header: () => 'Katalog Kodu',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false }),
      size: 120,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Katalog Kodu',
          detachable: true,
          sortable: true,
          visible: false,
          order: COLUMN_NAMES.indexOf(COLUMNS.CATALOG_ID),
        },
      },
    }),

    columnHelper.accessor('preparingTime', {
      header: () => 'Hazırlık Süresi',
      cell: ({ getValue }) => h(Label, { content: getValue() + ' Gün', isBold: false }),
      size: 120,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Hazırlık Süresi',
          detachable: true,
          sortable: true,
          visible: false,
          order: COLUMN_NAMES.indexOf(COLUMNS.PREPARING_TIME),
        },
      },
    }),

    columnHelper.accessor('shipmentTemplate', {
      header: () => 'Teslimat Şablonu',
      cell: ({ getValue }) => h(TooltipPlugin, {
        tooltipText: getValue(),
      }, {
        'tooltip-btn': h('label', { class: "tooltip not-bold", innerHTML: getValue() }),
      }),
      size: 106,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Teslimat Şablonu',
          detachable: true,
          sortable: true,
          visible: false,
          order: COLUMN_NAMES.indexOf(COLUMNS.SHIPMENT_TEMPLATE),
        },
      },
    }),

    columnHelper.accessor('senderTR', {
      header: 'Gönderici',
      cell: ({ getValue }) => h(Sender, {
        senderTR: getValue(),
      }),
      size: 132,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Gönderici',
          detachable: true,
          sortable: true,
          visible: false,
          order: COLUMN_NAMES.indexOf(COLUMNS.SENDER_TR),
        },
      },
    }),

    columnHelper.accessor('groupId', {
      header: () => 'Grup Kodu',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false }),
      size: 120,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Grup Kodu',
          detachable: true,
          sortable: true,
          visible: false,
          order: COLUMN_NAMES.indexOf(COLUMNS.GROUP_ID),
        },
      },
    }),

    columnHelper.accessor('barcode', {
      header: () => h(TableHeadTooltip, {
        title: 'GTIN',
        tooltipText: 'Barkod/GTIN, ürünün üzerinde yer alan kod numarasıdır ve her ürün seçeneği (varyant) için tanımlanır.',
        style: 'justify-content: space-between',
        tooltipWidth: 320,
      }),
      cell: ({ getValue }) => h(Label, { content: getValue() || '' }),
      size: 125,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'GTIN',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.GTIN),
        },
      },
    }),

    columnHelper.accessor('productMainId', {
      header: () => 'Model Kodu',
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => h(Label, { content: getValue() || '' }),
      size: 113,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Model Kodu',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.MODEL_CODE),
        },
      },
    }),

    columnHelper.accessor('id', {
      header: () => 'Ürün Kodu',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false }),
      size: 113,
      columnDef: {
        meta: {
          title: 'Ürün Kodu',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.PRODUCT_CODE),
        },
      },
    }),

    columnHelper.accessor('brand', {
      header: () => 'Marka',
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false }),
      size: 113,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Marka',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.BRAND),
        },
      },
    }),

    columnHelper.accessor('vatRate', {
      header: () => 'KDV Oranı',
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false, percentage: true, }),
      size: 112,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'KDV Oranı',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.VAT_RATES),
        },
      },
    }),

    columnHelper.accessor('categoryName', {
      header: () => 'Kategori Adı',
      cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false }),
      size: 113,
      columnDef: {
        meta: {
          title: 'Kategori Adı',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.CATEGORY_NAME),
        },
      },
    }),
    columnHelper.accessor('listPrice', {
      header: () => 'Piyasa Satış Fiyatı',
      cell: ({ row, getValue }) => {
        const isDisabled = ['Prohibited', 'InCatalogApproval', 'CatalogRejected'].includes(row.original.status);
        return h(PopoverInput, {
          value: getValue(),
          text: 'Piyasa Satış Fiyatı',
          inputId: `listPrice-${row.original.id}`,
          isValid: true,
          validate: () => true,
          format: formatPrice,
          isPrice: true,
          isDisabled: isDisabled,
          isActionClick: isUpdateProductsLoading,
          onUpdate: async (value, callback) => {
            try {
              await handleUpdate(store, {
                stockCode: row.original.stockCode,
                stock: row.original.quantity,
                salesPrice: row.original.salesPrice,
                listPrice: value,
                id: `listPrice-${row.original.id}`
              }, 'Piyasa Satış Fiyatı');
            } catch (e) {
              console.error(e);
              callback();
            }
          },
        });
      },
      size: 154,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Piyasa Satış Fiyatı',
          detachable: false,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.LIST_PRICE),
        },
      },
    }),

    columnHelper.accessor('salesPrice', {
      header: () => 'Satış Fiyatı',
      cell: ({ getValue, row }) => {
        const isDisabled = ['Prohibited', 'InCatalogApproval', 'CatalogRejected'].includes(row.original.status);
        const listPrice = row.original.listPrice;
        const maxPrice = row.original.maxPrice;
        const salesPrice = row.original.salesPrice;
        const isValid = validatePrice(getValue(), listPrice, maxPrice);
        updateFormValidity(isValid);
        return h(PopoverInput, {
          value: getValue(),
          text: 'n11 Satış Fiyatı',
          inputId: `salesPrice-${row.original.id}`,
          isValid: isValid,
          isPrice: true,
          isDisabled: isDisabled,
          isActionClick: isUpdateProductsLoading,
          validate: value => {
            let valid = false;

            valid = validatePrice(value, listPrice, maxPrice, salesPrice);
            updateFormValidity(valid);
            return valid;
          },
          format: formatPrice,
          onUpdate: async (value, callback) => {
            try {
              await handleUpdate(store, {
                stockCode: row.original.stockCode,
                stock: row.original.quantity,
                salesPrice: value,
                listPrice: row.original.listPrice,
                id: `salesPrice-${row.original.id}`
              }, 'n11 Satış Fiyatı');
            } catch (e) {
              console.error(e);
              callback();
            }
          },
        });
      },
      size: 139,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Satış Fiyatı',
          detachable: false,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.SALES_PRICE),
        },
      },
    }),

    columnHelper.accessor('quantity', {
      header: () => 'Stok',
      sortingFn: 'basic',
      cell: ({ row, getValue }) => {
        const isDisabled = ['Prohibited', 'InCatalogApproval', 'CatalogRejected'].includes(row.original.status);
        return h(PopoverInput, {
          value: getValue(),
          text: 'Stok',
          isDisabled: isDisabled,
          isValid: true,
          validate: value => {
            let valid = false;
            valid = validateStock(value);
            updateFormValidity(valid);

            return valid;
          },
          inputId: `quantity-${row.original.id}`,
          isActionClick: isUpdateProductsLoading,
          onUpdate: async (value, callback) => {
            try {
              await handleUpdate(store, {
                stockCode: row.original.stockCode,
                stock: parseFloat(value),
                salesPrice: row.original.salesPrice,
                listPrice: row.original.listPrice,
                id: `quantity-${row.original.id}`
              }, 'Stok');
            } catch (e) {
              console.error(e);
              callback();
            }
          },
        });
      },
      size: 111,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Stok',
          detachable: false,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.STOCK),
        },
      },
    }),

    columnHelper.accessor('buyBoxPrice', {
      header: () => h('div', { class: 'buybox-head' }, 'Buybox Kazanan Fiyat'),
      cell: ({ getValue }) => {
        const v = getValue();
        const text =
          v === null || v === undefined || v === '' || v === '-'
            ? '-'
            : formatPrice(v);
        return h(Label, { content: text, isBold: false });
      },
      size: 180,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'Buybox Kazanan Fiyat',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.BUYBOX_PRICE),
        },
      },
    }),

    columnHelper.accessor('status', {
      header: 'Durum',
      cell: ({ row, getValue }) => h(Status, {
        pid: String(row.original.id),
        groupId: String(row.original.groupId ?? ""),
        type: getValue(),
      }),
      size: 132,
      columnDef: {
        meta: {
          title: 'Durum',
          detachable: true,
          sortable: true,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.STATUS),
        },
      },
    }),

    columnHelper.accessor('actions', {
      header: 'İşlemler',
      cell: ({ row }) => {
        const pid = row.original.id;
        const status = row.original.status;
        const isMatchPending = row.original.inApprovalReason.some(item => item.value === 'MATCH_PENDING');

        if (status === 'InApproval' && !isMatchPending) {
          return h(ActionButtons, {
            pid,
            refreshCallback,
          });
        }

        const isDisabled = ['InCatalogApproval'].includes(status);
        const buttonStyle = isDisabled ? "pointer-events: none; opacity: 0.5;" : '';

        const handleClick = () => {
          if (pid && status && !isDisabled) {
            handleButtonClick(store, router, pid, isMatchPending);
          }
        };

        return h(ActionButton, {
          onClick: handleClick,
          style: buttonStyle,
          disabled: isDisabled,
        });
      },
      size: 96,
      enableResizing: false,
      columnDef: {
        meta: {
          title: 'İşlemler',
          detachable: false,
          sortable: false,
          visible: true,
          order: COLUMN_NAMES.indexOf(COLUMNS.ACTIONS),
        },
      },
    }),
  ]);

  if (activeTab === 'pending_actions') {
    columns.value = [
      ...columns.value,
      columnHelper.accessor('inApprovalReason', {
        header: () => 'Hata Etiketi ve Açıklama',
        sortingFn: 'alphanumeric',
        cell: ({ getValue }) => h(ErrorLabel, { content: getValue(), isBold: false }),
        size: 260,
        enableResizing: false,
        columnDef: {
          meta: {
            title: 'Hata Etiketi ve Açıklama',
            detachable: true,
            sortable: true,
            visible: true,
            order: COLUMN_NAMES.indexOf(COLUMNS.LABELS),
          },
        },
      }),
    ];
  }

  if (activeTab === 'catalog_rejected') {
    columns.value = [
      ...columns.value,
      columnHelper.accessor('rejectInfo', {
        header: () => 'Red Sebebi',
        sortingFn: 'alphanumeric',
        cell: ({ getValue }) => h(Label, { content: getValue(), isBold: false, style: { padding: '16px 0' } }),
        size: 190,
        enableResizing: false,
        columnDef: {
          meta: {
            title: 'Red Sebebi',
            detachable: true,
            sortable: true,
            visible: true,
            order: COLUMN_NAMES.indexOf(COLUMNS.REJECT_RESON),
          },
        },
      }),
    ];
  }

  function generateCheckboxHeader(props) {
    const { table } = props;

    return h(Checkbox, {
      id: 'products-table-selectAll',
      checked: table.getIsAllRowsSelected(),
      indeterminate: table.getIsSomeRowsSelected(),
      onChange: (event) => {
        const isChecked = event.target.checked ? 'selectAll' : 'deselectAll';
        table.toggleAllRowsSelected();
        table.options.meta.onCheckboxChange(table.getRowModel().rows, isChecked);
      },
    }, { default: () => [] });
  }

  function generateCheckboxCell(props) {
    const { row, table } = props;

    return h(Checkbox, {
      id: `product-${row.original.id}`,
      value: row.original.id,
      productId: `product-${row.idproductId}`,
      indeterminate: row.getIsSomeSelected(),
      checked: row.getIsSelected(),
      checkBoxDisabled: !row.getCanSelect(),
      onChange: () => {
        row.toggleSelected();
        table.options.meta.onCheckboxChange(row, 'singleChecked');
      },
    }, { default: () => [] });
  }

  const generateProductInfo = (row) => {
    return {
      imageUrl: row.original.productInfo.imageUrl,
      title: row.original.productInfo.title,
      commissionRate: row.original.commissionRateValue,
      commission: row.original.productInfo.commission,
    };
  };

  return columns;
}
